import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import qs from "query-string";
import {
    Button,
    Checkbox,
    Dropdown,
    FormGroup,
    Input,
    OptionType,
    RoleType,
    useTitle,
} from "modules/common";
import {
    TermsOfUseModal,
    AccountMessages,
    useEpoljoprivredaData,
    registerUser,
    decodeJwt,
    setSignupNiasData,
    clearSignupNiasData,
} from "modules/auth";
import { ReactComponent as Spinner } from "assets/icons/spinner.svg";
import { RootState } from "store";
import { useForm } from "modules/common/hooks/useForm";
import {
    emailFieldValidator,
    oibFieldValidator,
    requiredFieldValidator,
} from "modules/common/utils/formValidators";
import { CitizenForm, CitizenFormData } from "modules/auth/components/CitizenForm";
import { ErrorMessage } from "modules/common/components/FormError/FormError";
import { EPSignUpRequest, SignUpRequest } from "services";
import { useCheckEpUserRole } from "modules/auth/hooks/authHooks";
import { getEPoljoprivredaUrl } from "helpers/EnvironmentHelper";

const roleOptions: OptionType<RoleType>[] = [
    { value: RoleType.Citizen, label: "Građanin" },
    { value: RoleType.Farmer, label: "Poljoprivrednik" },
    { value: RoleType.PrivateAdviser, label: "Privatni Savjetnik" },
];

interface SignUpFormData {
    email: string;
    role: OptionType<RoleType> | null;
    termsAccepted: boolean;
}

export const SignUp: React.FC = () => {
    useTitle("Registracija - eSavjetnik");
    const dispatch = useDispatch();
    const [showToUModal, setShowToUModal] = useState(false);
    const [preFilledData, setpreFilledData] = useState({
        oib: false,
        firstName: false,
        lastName: false,
    });
    const { token, returnUrl } = useEpoljoprivredaData();
    const location = useLocation();
    const registration = useSelector((state: RootState) => state.registration);
    const checkEpUserRole = useCheckEpUserRole();

    const { registrationError, registrationSuccess, errorMessage, isLoading } = registration;

    const urlParams = useMemo(() => {
        const params = qs.parse(location.search);
        
        if (params.returnUrl && typeof params.returnUrl === 'string') {
            const returnUrlQueryString = params.returnUrl.split('?')[1];
            if (returnUrlQueryString) {
                const returnUrlParams = qs.parse(returnUrlQueryString);
                return {
                    forLegalIps: (returnUrlParams.ForLegalIps as string) || null,
                    forPersonOib: (returnUrlParams.ForPersonOib as string) || null,
                };
            }
        }
        
        return {
            forLegalIps: (params.ForLegalIps as string) || null,
            forPersonOib: (params.ForPersonOib as string) || null,
        };
    }, [location.search]);

    const signUpForm = useForm<SignUpFormData>({
        initialValue: {
            email: "",
            role: roleOptions[0],
            termsAccepted: false,
        },
        validators: {
            email: [requiredFieldValidator("Email je obavezan"), emailFieldValidator()],
            role: [requiredFieldValidator("Kategorija korisnika je obavezna")],
            termsAccepted: [requiredFieldValidator("Morate prihvatiti Uvjete korištenja")],
        },
    });
    const { formData: signUpData } = signUpForm;
    const citizenForm = useForm<CitizenFormData>({
        initialValue: {
            oib: "",
            firstName: "",
            lastName: "",
        },
        validators: {
            oib: [requiredFieldValidator("OIB je obavezan"), oibFieldValidator()],
            firstName: [requiredFieldValidator("Ime je obavezno")],
            lastName: [requiredFieldValidator("Prezime je obavezno")],
        },
    });
    const showCitizenForm = useMemo(
        () => signUpData.role?.value === RoleType.Citizen,
        [signUpData.role?.value]
    );

    useEffect(() => {
        if (!token) return;
        
        try {
            const decodedToken = decodeJwt(token);
            const { NavToken, MessageId, oib } = decodedToken;
            let { ForPersonOib, ForLegalIps, ToLegalIps, ToLegalIzvorReg, ForLegalIzvorReg } = decodedToken;
            
            if (returnUrl) {
                const returnUrlQueryString = returnUrl.split('?')[1];
                if (returnUrlQueryString) {
                    const returnUrlParams = new URLSearchParams(returnUrlQueryString);
                    ForPersonOib = returnUrlParams.get("ForPersonOib") || ForPersonOib;
                    ForLegalIps = returnUrlParams.get("ForLegalIps") || ForLegalIps;
                    ToLegalIps = returnUrlParams.get("ToLegalIps") || ToLegalIps;
                    ToLegalIzvorReg = returnUrlParams.get("ToLegalIzvor_reg") || ToLegalIzvorReg;
                    ForLegalIzvorReg = returnUrlParams.get("ForLegalIzvor_reg") || ForLegalIzvorReg;
                }
            }
            
            dispatch(setSignupNiasData({
                navToken: NavToken,
                messageId: MessageId,
                forPersonOib: ForPersonOib,
                forLegalIps: ForLegalIps,
                toLegalIps: ToLegalIps,
                toLegalIzvorReg: ToLegalIzvorReg,
                forLegalIzvorReg: ForLegalIzvorReg,
                oib: oib,
            }));
        } catch (error) {
            console.error("Error decoding JWT token:", error);
        }
    }, [token, returnUrl, dispatch]);

    useEffect(() => {
        if (!token) {
            dispatch(clearSignupNiasData());
            return;
        }
        
        try {
            const decodedToken = decodeJwt(token);
            const { email, email_verified } = decodedToken;
            
            if (email_verified && email) {
                signUpForm.onFormChange({ email });
            }
        } catch (error) {
            console.error("Error decoding JWT token:", error);
            dispatch(clearSignupNiasData());
        }
        
        checkEpUserRole.mutateAsync(token).then(role => {
            if (!role) return;

            const option = roleOptions.find(({ value }) => value === role);
            if (!option) return;

            signUpForm.onFormChange({ role: option });
        });

        const { forPersonOib, forLegalIps } = urlParams;
        const shouldShowCitizen = !forLegalIps && Boolean(forPersonOib);

        if (shouldShowCitizen && forPersonOib) {
            const citizenOption = roleOptions.find(({ value }) => value === RoleType.Citizen);
            if (citizenOption) {
                signUpForm.onFormChange({ role: citizenOption });
            }

            try {
                const decodedToken = decodeJwt(token);
                const { oib, given_name, family_name } = decodedToken;

                const preFilledOib = oib ?? "";
                const preFilledFirstName = given_name ?? "";
                const preFilledLastName = family_name ?? "";

                citizenForm.onFormChange({
                    oib: preFilledOib,
                    firstName: preFilledFirstName,
                    lastName: preFilledLastName,
                });

                setpreFilledData({
                    oib: Boolean(preFilledOib),
                    firstName: Boolean(preFilledFirstName),
                    lastName: Boolean(preFilledLastName),
                });
            } catch (error) {
                console.error("Error decoding JWT token:", error);
                citizenForm.onFormChange({
                    oib: forPersonOib,
                    firstName: "",
                    lastName: "",
                });
                
                setpreFilledData({
                    oib: Boolean(forPersonOib),
                    firstName: false,
                    lastName: false,
                });
            }
        }
    }, [token, returnUrl, urlParams, dispatch]);

    useEffect(() => {
        if (!registrationSuccess) return;

        dispatch(clearSignupNiasData());
        
        const url = new URL(`${getEPoljoprivredaUrl()}aplikacije`);
        window.location.href = url.toString();
    }, [registrationSuccess, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearSignupNiasData());
        };
    }, [dispatch]);

    const toggleToUModal = () => {
        setShowToUModal(!showToUModal);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const signUpDataValid = await signUpForm.validateForm();
        const citizenDataValid = !showCitizenForm || (await citizenForm.validateForm());
        if (!signUpDataValid || !citizenDataValid) return;

        let data: SignUpRequest | EPSignUpRequest;
        const citizenData = citizenForm.formData;

        const { email, role } = signUpData;
        if (showCitizenForm)
            data = {
                ...citizenData,
                email,
                countyId: citizenData.county?.value,
                settlementId: citizenData.settlement?.value,
                returnUrl
            } as SignUpRequest;
        else data = { email, role: role!.value, token, returnUrl } as EPSignUpRequest;

        dispatch(registerUser(data));
    };

    return (
        <div>
            <h1 className="signup-heading mt-48">Registracija</h1>
            {(registrationError || registrationSuccess) && (
                <AccountMessages
                    error={registrationError}
                    success={registrationSuccess}
                    message={errorMessage}
                />
            )}
            <form onSubmit={handleSubmit}>
                <FormGroup>
                    {signUpForm.labelFor("role", "Kategorija korisnika")}
                    <Dropdown
                        isDisabled={isLoading}
                        maxMenuHeight={200}
                        placeholder="Odaberite"
                        {...signUpForm.dropdownPropsFor("role", roleOptions)}
                    />

                    {signUpForm.labelFor("email", "Email adresa")}
                    <Input
                        isDisabled={isLoading}
                        placeholder="korisnik@opg.hr"
                        {...signUpForm.inputPropsFor("email")}
                    />

                    {showCitizenForm ? (
                        <CitizenForm 
                            isLoading={isLoading} 
                            form={citizenForm}
                            isOibReadOnly={preFilledData.oib}
                            isFirstNameReadOnly={preFilledData.firstName}
                            isLastNameReadOnly={preFilledData.lastName}
                        />
                    ) : null}
                </FormGroup>

                <div className="consent">
                    <Checkbox
                        type="checkbox"
                        isChecked={signUpData.termsAccepted}
                        onChange={() =>
                            signUpForm.onFormChange({
                                termsAccepted: !signUpData.termsAccepted,
                            })
                        }
                        className="checkbox--normal"
                        isDisabled={isLoading}
                        htmlFor="consent"
                        text={
                            <div className="consent__text">
                                Pristajem na{" "}
                                <a onClick={toggleToUModal} className="consent__link">
                                    Uvjete korištenja
                                </a>
                            </div>
                        }
                    />
                    <div className="consent__required__error">
                        <ErrorMessage error={signUpForm.errors.termsAccepted} />
                    </div>
                </div>
                {isLoading ? (
                    <div className="ml-80">
                        <Spinner className="spinner" />
                    </div>
                ) : (
                    <Button type="submit" variant="primary" size="large" className="mb-84">
                        <span>Registriraj se</span>
                    </Button>
                )}
            </form>

            <TermsOfUseModal toggleModal={toggleToUModal} isVisible={showToUModal} />
        </div>
    );
};
